import React, { useMemo, useState } from 'react';
import { ComposableMap, Geographies, Geography, Marker, ZoomableGroup } from 'react-simple-maps';
import { useTheme } from '@mui/material/styles';
import { Box, IconButton, Stack, Paper, Typography, List, ListItem, ListItemText } from '@mui/material';
import { ZoomIn as ZoomInIcon, ZoomOut as ZoomOutIcon, CenterFocusStrong as CenterIcon } from '@mui/icons-material';
import { getRegionCoordinates, getCountryCodeForRegion, enhanceNodeWithLocationData } from '../api/services/computeService/LocationService';

const geoUrl = require('./world-countries.json');

export default function MapChart({ servers }) {
  const theme = useTheme();
  const [position, setPosition] = useState({ coordinates: [-25, 50], zoom: 1 });
  const [hoveredCluster, setHoveredCluster] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [mapContainer, setMapContainer] = useState(null);

  const clusteredMarkers = useMemo(() => {
    const coordinateGroups = {};
    
    servers.forEach(server => {
      const enhancedServer = enhanceNodeWithLocationData(server);
      const baseCoords = enhancedServer.coordinates || getRegionCoordinates(server.region);
      
      if (!baseCoords || !Array.isArray(baseCoords) || baseCoords.length < 2 || 
          baseCoords[0] === 0 || baseCoords[1] === 0 || 
          baseCoords[0] === undefined || baseCoords[1] === undefined ||
          isNaN(baseCoords[0]) || isNaN(baseCoords[1])) {
        return;
      }
      
      const coordKey = `${baseCoords[0].toFixed(4)},${baseCoords[1].toFixed(4)}`;
      
      if (!coordinateGroups[coordKey]) {
        coordinateGroups[coordKey] = [];
      }
      
      coordinateGroups[coordKey].push(enhancedServer);
    });
    
    const clusters = [];
    
    Object.values(coordinateGroups).forEach(group => {
      const firstServer = group[0];
      const baseCoords = firstServer.coordinates || getRegionCoordinates(firstServer.region);
      
      if (!baseCoords || !Array.isArray(baseCoords) || baseCoords.length < 2 || 
          baseCoords[0] === 0 || baseCoords[1] === 0 || 
          baseCoords[0] === undefined || baseCoords[1] === undefined) {
        return;
      }
      
      const statusCounts = group.reduce((acc, server) => {
        const status = server.status || 'Unknown';
        acc[status] = (acc[status] || 0) + 1;
        return acc;
      }, {});
      
      const dominantStatus = Object.keys(statusCounts).reduce((a, b) => 
        statusCounts[a] > statusCounts[b] ? a : b
      );
      
      clusters.push({
        id: `cluster-${baseCoords[0]}-${baseCoords[1]}`,
        coordinates: baseCoords,
        nodes: group,
        count: group.length,
        dominantStatus: dominantStatus
      });
    });
    
    return clusters;
  }, [servers]);

  const handleZoomIn = () => {
    if (position.zoom >= 4) return;
    setPosition(pos => ({ ...pos, zoom: pos.zoom * 1.5 }));
  };

  const handleZoomOut = () => {
    if (position.zoom <= 0.5) return;
    setPosition(pos => ({ ...pos, zoom: pos.zoom / 1.5 }));
  };

  const handleReset = () => {
    setPosition({ coordinates: [-25, 50], zoom: 1 });
  };



  return (
    <Box 
      ref={setMapContainer}
      sx={{ position: 'relative', width: '100%', height: '100%' }}
      onMouseMove={(e) => {
        if (hoveredCluster && mapContainer) {
          const rect = mapContainer.getBoundingClientRect();
          setTooltipPosition({
            x: e.clientX - rect.left,
            y: e.clientY - rect.top
          });
        }
      }}
    >
      {/* Zoom Controls */}
      <Stack
        direction="column"
        spacing={1}
        sx={{
          position: 'absolute',
          top: 16,
          right: 16,
          zIndex: 1000,
          bgcolor: theme.palette.background.paper,
          borderRadius: 2,
          p: 1,
          boxShadow: theme.palette.mode === 'dark' ? '0 4px 12px rgba(0,0,0,0.5)' : '0 4px 12px rgba(0,0,0,0.15)',
          border: `1px solid ${theme.palette.divider}`
        }}
      >
        <IconButton size="small" onClick={handleZoomIn} disabled={position.zoom >= 4}>
          <ZoomInIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" onClick={handleZoomOut} disabled={position.zoom <= 0.5}>
          <ZoomOutIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" onClick={handleReset}>
          <CenterIcon fontSize="small" />
        </IconButton>
      </Stack>

      <ComposableMap
        projection="geoMercator"
        projectionConfig={{
          scale: 400,
          center: [-25, 50]
        }}
        style={{
          width: "100%",
          height: "100%",
          backgroundColor: theme.palette.background.default,
          cursor: 'grab'
        }}
      >
        <ZoomableGroup
          zoom={position.zoom}
          center={position.coordinates}
          onMoveEnd={setPosition}
          minZoom={0.5}
          maxZoom={4}
        >
          <Geographies geography={geoUrl}>
            {({ geographies }) =>
              geographies.map((geo) => {
                const serversInCountry = clusteredMarkers.filter(
                  cluster => cluster.nodes.some(node => node.location === geo.properties.ISO_A2)
                );
                const hasServer = serversInCountry.length > 0;

                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    fill={hasServer ? theme.palette.primary.main : theme.palette.grey[theme.palette.mode === 'dark' ? 800 : 200]}
                    stroke={theme.palette.grey[theme.palette.mode === 'dark' ? 700 : 400]}
                    strokeWidth={0.5 / position.zoom}
                    style={{
                      default: { outline: 'none' },
                      hover: {
                        outline: 'none',
                        fill: hasServer ? theme.palette.primary.dark : theme.palette.grey[theme.palette.mode === 'dark' ? 800 : 300]
                      },
                      pressed: { outline: 'none' }
                    }}
                  />
                );
              })
            }
          </Geographies>

          {clusteredMarkers.map((cluster) => (
            <Marker 
              key={cluster.id} 
              coordinates={cluster.coordinates}
            >
              <g
                onMouseEnter={() => setHoveredCluster(cluster)}
                onMouseLeave={() => setHoveredCluster(null)}
                style={{ cursor: 'pointer' }}
              >
                <g
                  fill={cluster.dominantStatus === "Active" ? theme.palette.success.main : cluster.dominantStatus === "Registering" ? theme.palette.warning.main : theme.palette.error.main}
                  stroke={theme.palette.text.primary}
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  transform="translate(-12, -24)"
                >
                  <circle cx="12" cy="10" r={cluster.count > 1 ? 5 : 4} />
                  <path d="M12 21.7C17.3 17 20 13 20 10a8 8 0 1 0-16 0c0 3 2.7 6.9 8 11.7z" />
                </g>
                {cluster.count > 1 && (
                  <>
                    <circle
                      cx="0"
                      cy="-24"
                      r={Math.max(10, 12 / position.zoom)}
                      fill={theme.palette.background.paper}
                      stroke={theme.palette.text.primary}
                      strokeWidth="2"
                    />
                    <text
                      textAnchor="middle"
                      y={-19}
                      style={{
                        fontFamily: "system-ui",
                        fill: theme.palette.primary.main,
                        fontSize: Math.max(9, 11 / position.zoom) + "px",
                        fontWeight: "bold"
                      }}
                    >
                      {cluster.count}
                    </text>
                  </>
                )}
                {cluster.count === 1 && (
                  <text
                    textAnchor="middle"
                    y={-30}
                    style={{
                      fontFamily: "system-ui",
                      fill: theme.palette.text.primary,
                      fontSize: Math.max(10, 12 / position.zoom) + "px",
                      fontWeight: "bold",
                      textShadow: theme.palette.mode === 'dark' ? "0 0 4px rgba(0,0,0,0.7)" : "0 0 4px rgba(255,255,255,0.7)"
                    }}
                  >
                    {cluster.nodes[0].hostname || cluster.nodes[0].name || `Node-${cluster.nodes[0].id}`}
                  </text>
                )}
              </g>
            </Marker>
          ))}
        </ZoomableGroup>
      </ComposableMap>

      {hoveredCluster && hoveredCluster.count > 1 && (
        <Paper
          onMouseEnter={() => setHoveredCluster(hoveredCluster)}
          onMouseLeave={() => setHoveredCluster(null)}
          sx={{
            position: 'absolute',
            left: `${tooltipPosition.x}px`,
            top: `${tooltipPosition.y - 10}px`,
            transform: 'translate(-50%, -100%)',
            zIndex: 2000,
            p: 1.5,
            maxWidth: 300,
            maxHeight: hoveredCluster.count > 10 ? 300 : 'auto',
            overflowY: hoveredCluster.count > 10 ? 'auto' : 'visible',
            overflowX: 'hidden',
            bgcolor: theme.palette.background.paper,
            boxShadow: theme.palette.mode === 'dark' ? '0 4px 20px rgba(0,0,0,0.7)' : '0 4px 20px rgba(0,0,0,0.2)',
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
            pointerEvents: 'auto',
            mb: 1
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', fontSize: '0.875rem' }}>
            {hoveredCluster.count} Node{hoveredCluster.count > 1 ? 's' : ''} at this location
          </Typography>
          <List dense sx={{ py: 0, maxHeight: hoveredCluster.count > 10 ? 250 : 'none' }}>
            {hoveredCluster.nodes.map((node, index) => (
              <ListItem key={node.id || index} sx={{ px: 1, py: 0.5 }}>
                <ListItemText
                  primary={node.hostname || node.name || `Node-${node.id}`}
                  secondary={node.status || 'Unknown'}
                  primaryTypographyProps={{
                    fontSize: '0.8rem',
                    sx: {
                      color: node.status === "Active" ? theme.palette.success.main : 
                             node.status === "Registering" ? theme.palette.warning.main : 
                             theme.palette.error.main
                    }
                  }}
                  secondaryTypographyProps={{
                    fontSize: '0.7rem'
                  }}
                />
              </ListItem>
            ))}
          </List>
        </Paper>
      )}
    </Box>
  );
}
